/**
 * QuillHealthContext
 * ===================
 * One health check on mount against GET /api/admin/quill/health (proxied to
 * quill-admin-backend by webpack.config.js's dev-server proxy). No polling —
 * the user hits Retry if the backend comes up after the fact.
 *
 * Statuses:
 *  - 'checking'     initial state, request in flight
 *  - 'up'           backend responded 200 with {success:true}
 *  - 'down'         network error, timeout, or non-200/non-401 response
 *                    (backend not running, or the dev-server proxy target is
 *                    unreachable)
 *  - 'unauthorized' backend responded 401 — it IS running, but
 *                    QUILL_INTERNAL_API_KEY on the backend doesn't match the
 *                    key the dev-server proxy is injecting
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { quillApi, QuillBackendStatus } from '../../services/quillAdminService';

export interface QuillHealthContextValue {
  status: QuillBackendStatus | 'checking';
  /** Re-run the health check (used by the Retry button in the down/unauthorized empty state). */
  recheck: () => void;
}

const QuillHealthContext = createContext<QuillHealthContextValue | undefined>(undefined);

export const QuillHealthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<QuillBackendStatus | 'checking'>('checking');

  const recheck = useCallback(() => {
    let cancelled = false;
    setStatus('checking');
    quillApi.health.check().then((result) => {
      if (!cancelled) setStatus(result.status);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return recheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<QuillHealthContextValue>(() => ({ status, recheck }), [status, recheck]);

  return <QuillHealthContext.Provider value={value}>{children}</QuillHealthContext.Provider>;
};

export function useQuillHealth(): QuillHealthContextValue {
  const ctx = useContext(QuillHealthContext);
  if (!ctx) {
    throw new Error('useQuillHealth must be used within a QuillHealthProvider');
  }
  return ctx;
}

export default QuillHealthContext;
